import { RegistroDto } from "@/api";
import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Card,
  FormHelperText,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Control, Controller, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  discoverRicette,
  registroRicetta,
  RICETTA_PARAMETRI,
  RicettaDiscovered,
  RicettaParamConfig,
} from "./GFL_TFLD_550_2_ricette";

// Elenco delle ricette + dettaglio dei parametri della ricetta selezionata.
//
// I pannelli non selezionati sono nascosti con display:none invece di essere
// smontati: in questo modo react-hook-form li valida indipendentemente da
// quale tab e` selezionato.
//
// Il nome di ogni campo del form coincide col nome del registro: e` su questa
// invariante che RegistriMqttDialog costruisce il diff e invia al PLC, con un
// solo salvataggio, i soli valori modificati (anche di ricette diverse).

function nomiCampiRicetta(indiceRaw: string) {
  return RICETTA_PARAMETRI.map((param) =>
    registroRicetta(indiceRaw, param.suffisso)
  );
}

const CAMPO_NOME_RICETTA = RICETTA_PARAMETRI[0].suffisso;

export function RicetteCard_GFL_TFLD_550_2({
  registri,
  control,
  readOnly,
}: {
  registri?: RegistroDto[];
  control: Control<any>;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  const ricette = useMemo(() => discoverRicette(registri), [registri]);
  const [selezionata, setSelezionata] = useState<string | null>(null);

  useEffect(() => {
    if (ricette.length === 0) {
      setSelezionata((precedente) => (precedente === null ? precedente : null));
      return;
    }

    setSelezionata((precedente) =>
      precedente !== null &&
      ricette.some((ricetta) => ricetta.indiceRaw === precedente)
        ? precedente
        : ricette[0].indiceRaw
    );
  }, [ricette]);

  const handleSelect = useCallback(
    (indiceRaw: string) => setSelezionata(indiceRaw),
    []
  );

  return (
    <Card>
      <Box p={2}>
        <Typography variant="h5" color="text" fontWeight="medium" gutterBottom>
          {t("gflTfld5502.ricette.titolo")}
        </Typography>

        {ricette.length === 0 ? (
          <Typography variant="button" fontWeight="regular" color="text">
            {t("gflTfld5502.ricette.vuoto")}
          </Typography>
        ) : (
          <>
            <RicetteStatoGlobale
              control={control}
              ricette={ricette}
              onRicettaInErrore={handleSelect}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  gutterBottom
                  display="block"
                >
                  {t("gflTfld5502.ricette.elenco")}
                </Typography>
                <List dense disablePadding>
                  {ricette.map((ricetta) => (
                    <RicettaListItem
                      key={ricetta.indiceRaw}
                      control={control}
                      ricetta={ricetta}
                      selected={ricetta.indiceRaw === selezionata}
                      onSelect={handleSelect}
                    />
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={8}>
                {ricette.map((ricetta) => (
                  <RicettaDetailPanel
                    key={ricetta.indiceRaw}
                    control={control}
                    ricetta={ricetta}
                    visible={ricetta.indiceRaw === selezionata}
                    readOnly={readOnly}
                  />
                ))}
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Card>
  );
}

/**
 * Conteggio delle modifiche pendenti su tutte le ricette e selezione automatica
 * della prima ricetta in errore: senza di essa un errore su una ricetta non
 * visualizzata renderebbe il pulsante Salva apparentemente inerte, perche` il
 * dialog non passa un handler per il submit non valido.
 */
const RicetteStatoGlobale = memo(function RicetteStatoGlobale({
  control,
  ricette,
  onRicettaInErrore,
}: {
  control: Control<any>;
  ricette: RicettaDiscovered[];
  onRicettaInErrore: (indiceRaw: string) => void;
}) {
  const { t } = useTranslation();

  const campiPerRicetta = useMemo(
    () =>
      ricette.map((ricetta) => ({
        indiceRaw: ricetta.indiceRaw,
        campi: nomiCampiRicetta(ricetta.indiceRaw),
      })),
    [ricette]
  );
  const tuttiICampi = useMemo(
    () => campiPerRicetta.flatMap((ricetta) => ricetta.campi),
    [campiPerRicetta]
  );

  const { dirtyFields, errors } = useFormState({
    control,
    name: tuttiICampi,
  });

  const modifiche = tuttiICampi.filter((campo) =>
    Boolean((dirtyFields as any)[campo])
  ).length;

  const primaInErrore = campiPerRicetta.find((ricetta) =>
    ricetta.campi.some((campo) => Boolean((errors as any)[campo]))
  )?.indiceRaw;

  useEffect(() => {
    if (primaInErrore) {
      onRicettaInErrore(primaInErrore);
    }
  }, [primaInErrore, onRicettaInErrore]);

  if (modifiche === 0) {
    return null;
  }

  return (
    <Typography
      variant="button"
      fontWeight="regular"
      color="warning.main"
      display="block"
      mb={1}
    >
      {t("gflTfld5502.ricette.modifichePendenti", { numero: modifiche })}
    </Typography>
  );
});

const RicettaListItem = memo(function RicettaListItem({
  control,
  ricetta,
  selected,
  onSelect,
}: {
  control: Control<any>;
  ricetta: RicettaDiscovered;
  selected: boolean;
  onSelect: (indiceRaw: string) => void;
}) {
  const { t } = useTranslation();

  const campi = useMemo(
    () => nomiCampiRicetta(ricetta.indiceRaw),
    [ricetta.indiceRaw]
  );
  const { dirtyFields, errors } = useFormState({ control, name: campi });
  const nome = useWatch({
    control,
    name: registroRicetta(ricetta.indiceRaw, CAMPO_NOME_RICETTA),
  });

  const modificata = campi.some((campo) =>
    Boolean((dirtyFields as any)[campo])
  );
  const inErrore = campi.some((campo) => Boolean((errors as any)[campo]));

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(ricetta.indiceRaw)}
    >
      <ListItemText
        primary={nomeRicettaVisualizzato(nome, t)}
        secondary={t("gflTfld5502.ricette.etichettaRicetta", {
          indice: ricetta.indice,
        })}
      />
      {inErrore ? (
        <Tooltip title={t("gflTfld5502.ricette.tooltipErrore")}>
          <ErrorOutlineIcon color="error" fontSize="small" />
        </Tooltip>
      ) : modificata ? (
        <Tooltip title={t("gflTfld5502.ricette.tooltipModificata")}>
          <FiberManualRecordIcon color="warning" fontSize="small" />
        </Tooltip>
      ) : null}
    </ListItemButton>
  );
});

const RicettaDetailPanel = memo(function RicettaDetailPanel({
  control,
  ricetta,
  visible,
  readOnly,
}: {
  control: Control<any>;
  ricetta: RicettaDiscovered;
  visible: boolean;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  const nome = useWatch({
    control,
    name: registroRicetta(ricetta.indiceRaw, CAMPO_NOME_RICETTA),
  });

  return (
    <Box sx={{ display: visible ? "block" : "none" }}>
      <Typography
        variant="button"
        fontWeight="medium"
        color="text"
        gutterBottom
        display="block"
      >
        {t("gflTfld5502.ricette.etichettaRicetta", { indice: ricetta.indice })}
        {" - "}
        {nomeRicettaVisualizzato(nome, t)}
      </Typography>

      <Grid container spacing={2}>
        {RICETTA_PARAMETRI.map((config) => (
          <Grid item xs={12} key={config.suffisso}>
            <RicettaParamField
              control={control}
              name={registroRicetta(ricetta.indiceRaw, config.suffisso)}
              config={config}
              readOnly={readOnly}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

const RicettaParamField = memo(function RicettaParamField({
  control,
  name,
  config,
  readOnly,
}: {
  control: Control<any>;
  name: string;
  config: RicettaParamConfig;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  if (config.kind === "text") {
    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="button" fontWeight="regular" color="text">
          {t(config.labelKey)}
        </Typography>
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState: { error } }) => (
            <Stack direction="column" alignItems="end" sx={{ width: "100%" }}>
              <TextField
                {...field}
                value={field.value ?? ""}
                size="small"
                fullWidth
                disabled={readOnly}
                error={!!error}
              />
              {error && <FormHelperText error>{error.message}</FormHelperText>}
            </Stack>
          )}
        />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="row" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text" flex={1}>
        {t(config.labelKey)}
      </Typography>
      <Controller
        control={control}
        name={name}
        rules={{
          validate: (value) => {
            if (isNil(value) || value === "") {
              return true;
            }

            const numeric = Number(value);
            if (!Number.isFinite(numeric)) {
              return t("gflTfld5502.ricette.validazione.numeroNonValido");
            }

            if (!isNil(config.min) && numeric < config.min) {
              return t("gflTfld5502.ricette.validazione.min", {
                min: config.min,
              });
            }

            if (!isNil(config.max) && numeric > config.max) {
              return t("gflTfld5502.ricette.validazione.max", {
                max: config.max,
              });
            }

            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Stack
            direction="column"
            alignItems="end"
            sx={{ width: "100%", maxWidth: 200 }}
          >
            <NumberInput
              {...field}
              numericInputProps={{ decimalScale: config.decimals ?? 0 }}
              endAdornment={
                config.unit ? (
                  <InputAdornment position="end">{config.unit}</InputAdornment>
                ) : undefined
              }
              disabled={readOnly}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </Stack>
        )}
      />
    </Box>
  );
});

function nomeRicettaVisualizzato(
  nome: unknown,
  t: (key: string) => string
): string {
  if (isNil(nome) || nome === "" || nome === "/") {
    return t("gflTfld5502.ricette.senzaNome");
  }
  return String(nome);
}
